'use client';

/**
 * StarRating — reusable 1–5 star component.
 *
 * Modes:
 *  - interactive (default): hoverable + clickable, calls onRate(value)
 *  - readonly: display-only, no hover/click
 *
 * Size variants: 'sm' (h-4 w-4), 'md' (h-5 w-5), 'lg' (h-6 w-6)
 */

import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  value?: number;           // Current rating (1–5), undefined = no rating
  onRate?: (value: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };

export function StarRating({
  value,
  onRate,
  readonly = false,
  size = 'md',
  className,
}: StarRatingProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const display = hovered ?? value ?? 0;

  return (
    <div
      className={cn('flex items-center gap-0.5', className)}
      role={readonly ? 'img' : 'group'}
      aria-label={value ? `${value} out of 5 stars` : 'Rate this item'}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Clicking the same star clears the rating
            onRate?.(star === value ? 0 : star);
          }}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(null)}
          className={cn(
            'transition-transform',
            !readonly && 'hover:scale-110 active:scale-95 cursor-pointer',
            readonly && 'cursor-default pointer-events-none'
          )}
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
        >
          <Star
            className={cn(
              SIZE[size],
              'transition-colors',
              star <= display
                ? 'fill-[oklch(0.78_0.16_75)] text-[oklch(0.78_0.16_75)]'  // gold/accent
                : 'fill-transparent text-muted-foreground/40'
            )}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}
